import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Box, ButtonBase, Typography } from "@mui/material";
import { FREE_RUNS, freeRunsLabel } from "@/utils/arcade/playGate";
import { playedToday, streakLabel } from "@/utils/arcade/progress";
import { utcDateKey } from "@/utils/arcade/dailyChallenge";
import type { ArcadeTheme } from "@/utils/arcade/theme";
import type { ArcadeRetention } from "./useArcadeRetention";

// One short line under the Start button: free plays left, the members note on
// the endless run, or the streak to keep alive.
export function StartCardNote({ retention, theme }: { retention: ArcadeRetention; theme: ArcadeTheme }) {
  const palette = theme.palette;
  const text = { fontFamily: "Poppins, sans-serif", fontSize: "13px", color: palette.text, lineHeight: 1.35 } as const;
  const today = utcDateKey(Date.now());

  if (retention.freeLeft !== null) {
    const line = retention.membersOnly ? "Members only, free with your email." : `${freeRunsLabel(retention.freeLeft)}.`;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", columnGap: "8px" }}>
        {retention.membersOnly ? null : (
          <Box component="span" aria-hidden sx={{ display: "inline-flex", gap: "3px" }}>
            {Array.from({ length: FREE_RUNS }, (_, dot) => (
              <Box key={dot} component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: dot < (retention.freeLeft ?? 0) ? palette.accent : "rgba(255,255,255,0.22)" }} />
            ))}
          </Box>
        )}
        <Typography component="span" sx={{ ...text, fontWeight: 600 }}>
          {line}
        </Typography>
        <ButtonBase
          onClick={() => retention.openDialog(retention.membersOnly ? "members-only" : "free-runs-used")}
          sx={{ ...text, minHeight: "32px", gap: "4px", opacity: 0.85, textDecoration: "underline", textUnderlineOffset: "3px", "&:hover": { opacity: 1 } }}
        >
          <LockOpenIcon sx={{ fontSize: 15 }} />
          Unlock free
        </ButtonBase>
      </Box>
    );
  }

  if (retention.streak > 0) {
    return (
      <Typography sx={{ ...text, display: "inline-flex", alignItems: "center", gap: "5px", opacity: 0.85 }}>
        <LocalFireDepartmentIcon sx={{ fontSize: 16, color: palette.accent }} />
        {streakLabel(retention.streak)}
        {playedToday(retention.progress, today) ? "" : ". Play today to keep it."}
      </Typography>
    );
  }
  return null;
}

export default StartCardNote;
